import React, { useEffect, useState } from 'react';
import { X, Save, RefreshCcw } from 'lucide-react';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal for editing an existing refund (manual or linked).
 *
 * Props:
 *  - refund: the refund object to edit
 *  - onClose: () => void
 *  - onSaved: (updatedRefund) => void
 */
export const EditRefundModal = ({ refund, onClose, onSaved }) => {
  const password = sessionStorage.getItem('admin_password');
  const isManual = refund?.source_type === 'manual';
  const [form, setForm] = useState({
    amount: '',
    reason: '',
    refund_date: '',
    client_name: '',
    client_email: '',
    currency: 'CI$',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!refund) return;
    setForm({
      amount: refund.amount?.toString() || '',
      reason: refund.reason || '',
      refund_date: (refund.refund_date || '').slice(0, 10),
      client_name: refund.client_name || '',
      client_email: refund.client_email || '',
      currency: refund.currency || 'CI$',
    });
    setError('');
  }, [refund]);

  if (!refund) return null;

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { setError('Please enter a valid refund amount.'); return; }
    if (!form.reason.trim()) { setError('Reason cannot be empty.'); return; }

    setSaving(true);
    try {
      const body = {
        amount: amt,
        reason: form.reason.trim(),
        refund_date: form.refund_date ? new Date(form.refund_date).toISOString() : refund.refund_date,
      };
      if (isManual) {
        body.client_name = form.client_name.trim();
        body.client_email = form.client_email.trim();
        body.currency = form.currency;
      }
      const res = await fetch(`${API_URL}/api/admin/refunds/${refund.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Basic ${btoa(':' + password)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Failed (HTTP ${res.status})`);
      }
      const updated = await res.json();
      onSaved && onSaved(updated);
      onClose();
    } catch (err) {
      logger.error('Edit refund failed:', err);
      setError(err.message || 'Failed to save refund.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
         onClick={onClose} data-testid="edit-refund-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 text-red-600 rounded-lg flex items-center justify-center">
              <RefreshCcw className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Edit Refund {refund.refund_number}</h2>
              <p className="text-xs text-foreground/60 uppercase tracking-wider">
                {refund.source_type.replace('_', ' ')} · {refund.source_number}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="edit-refund-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {isManual && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold mb-1">Client Name</label>
                <input type="text" value={form.client_name}
                       onChange={(e) => update('client_name', e.target.value)}
                       data-testid="edit-refund-client-name"
                       className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Client Email</label>
                <input type="email" value={form.client_email}
                       onChange={(e) => update('client_email', e.target.value)}
                       data-testid="edit-refund-client-email"
                       className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-semibold mb-1">Refund Amount *</label>
              <input type="number" step="0.01" min="0.01"
                     value={form.amount} onChange={(e) => update('amount', e.target.value)}
                     data-testid="edit-refund-amount"
                     className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
                     required />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1">Currency</label>
              {isManual ? (
                <select value={form.currency} onChange={(e) => update('currency', e.target.value)}
                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500">
                  <option value="CI$">CI$</option>
                  <option value="US$">US$</option>
                  <option value="£">£</option>
                  <option value="€">€</option>
                </select>
              ) : (
                <input type="text" value={form.currency} disabled
                       className="w-full px-3 py-2 border-2 border-gray-200 bg-gray-50 rounded-lg text-sm text-foreground/60" />
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Refund Date</label>
            <input type="date" value={form.refund_date}
                   onChange={(e) => update('refund_date', e.target.value)}
                   data-testid="edit-refund-date"
                   className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500" />
          </div>

          <div>
            <label className="block text-xs font-semibold mb-1">Reason *</label>
            <textarea value={form.reason} onChange={(e) => update('reason', e.target.value)}
                      rows={3}
                      data-testid="edit-refund-reason"
                      className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-red-500"
                      required />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="edit-refund-error">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
            <button type="submit" disabled={saving}
                    data-testid="edit-refund-submit-btn"
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2">
              <Save className={`w-4 h-4 ${saving ? 'animate-spin' : ''}`} />
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRefundModal;
